/**
 * Client-side search over the help-center articles (#2685).
 *
 * The corpus is ~70 short articles, so a small in-memory index is enough and
 * adds no dependency. Rules, pinned by `search.test.ts`:
 *   - case- and umlaut-insensitive ("loeschen", "loschen" and "löschen" match),
 *   - every query word must match (AND); a word matches as a prefix,
 *   - words of 5+ letters also match with one typo,
 *   - title hits rank above heading hits, which rank above body hits,
 *   - a result points at the best-matching heading when there is one.
 */
import { Block, Inline, collectHeadings, inlineText, parseMarkdown } from './markdown';
import { HelpArticleMeta } from './helpCenter';

export interface SearchResult {
    article: HelpArticleMeta;
    score: number;
    /** Heading anchor to jump to, if a heading matched. */
    anchor?: string;
    headingText?: string;
    snippet: string;
}

interface IndexedArticle {
    article: HelpArticleMeta;
    titleWords: string[];
    headings: Array<{ id: string, text: string, words: string[] }>;
    bodyText: string;
    bodyWords: Set<string>;
}

/** Lower-case, fold umlauts and ß, so "ä", "ae" and "a" compare equal. */
export const normalize = (value: string): string =>
    value
        .toLowerCase()
        .replace(/ä|ae/g, 'a')
        .replace(/ö|oe/g, 'o')
        .replace(/ü|ue/g, 'u')
        .replace(/ß/g, 'ss');

export const tokenize = (value: string): string[] =>
    normalize(value)
        .split(/[^a-z0-9]+/)
        .filter((w) => w.length >= 2);

/** Levenshtein distance, stopping early once it exceeds `max`. */
const withinDistance = (a: string, b: string, max: number): boolean => {
    if (Math.abs(a.length - b.length) > max) return false;
    let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
    for (let i = 1; i <= a.length; i += 1) {
        const row = [i];
        let rowMin = i;
        for (let j = 1; j <= b.length; j += 1) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            row[j] = Math.min(prev[j] + 1, row[j - 1] + 1, prev[j - 1] + cost);
            rowMin = Math.min(rowMin, row[j]);
        }
        if (rowMin > max) return false;
        prev = row;
    }
    return prev[b.length] <= max;
};

/** Does query word `q` match document word `w`? */
export const wordMatches = (q: string, w: string): boolean => {
    if (w.startsWith(q)) return true;
    if (q.length < 5) return false;
    // One typo, compared against the word itself or its prefix of equal length
    // (so a typo in a partially typed long word still matches).
    return withinDistance(q, w, 1) || withinDistance(q, w.slice(0, q.length), 1);
};

const anyMatch = (q: string, words: string[] | Set<string>): boolean => {
    let found = false;
    words.forEach((w) => {
        if (!found && wordMatches(q, w)) found = true;
    });
    return found;
};

const blocksText = (blocks: Block[]): string =>
    blocks
        .map((b) => {
            switch (b.type) {
                case 'heading':
                    return b.text;
                case 'paragraph':
                    return inlineText(b.children);
                case 'list':
                    return b.items.map((item) => blocksText(item.blocks)).join(' ');
                case 'table':
                    return [b.header, ...b.rows].map((row) => row.map((cell: Inline[]) => inlineText(cell)).join(' ')).join(' ');
                case 'blockquote':
                    return blocksText(b.blocks);
                case 'code':
                    return b.text;
                default:
                    return '';
            }
        })
        .join(' ')
        .replace(/\s+/g, ' ');

export const buildSearchIndex = (articles: HelpArticleMeta[], content: Record<string, string>): IndexedArticle[] =>
    articles.map((article) => {
        const blocks = parseMarkdown(content[article.slug] ?? '');
        const bodyText = blocksText(blocks);
        return {
            article,
            titleWords: tokenize(article.title),
            headings: collectHeadings(blocks)
                .filter((h) => h.level > 1)
                .map((h) => ({ id: h.id, text: h.text, words: tokenize(h.text) })),
            bodyText,
            bodyWords: new Set(tokenize(bodyText)),
        };
    });

const snippetAround = (text: string, queryWords: string[]): string => {
    const folded = normalize(text);
    let at = -1;
    for (const q of queryWords) {
        const found = folded.indexOf(q);
        if (found >= 0 && (at < 0 || found < at)) at = found;
    }
    // `normalize` can shorten the text (ae → a), so positions are approximate;
    // a snippet a few characters off is fine, a wrong article is not.
    const startAt = Math.max(0, at - 60);
    const raw = text.slice(startAt, startAt + 180).trim();
    const lead = startAt > 0 ? '… ' : '';
    const tail = startAt + 180 < text.length ? ' …' : '';
    return `${lead}${raw}${tail}`;
};

/** Score of one query word in one article: 0 = no match anywhere. */
const wordScore = (entry: IndexedArticle, q: string): number => {
    const inTitle = anyMatch(q, entry.titleWords);
    const inHeading = entry.headings.some((h) => anyMatch(q, h.words));
    const inBody = anyMatch(q, entry.bodyWords);
    const exactTitle = entry.titleWords.includes(q) ? 5 : 0;
    return (inTitle ? 10 : 0) + (inHeading ? 4 : 0) + (inBody ? 1 : 0) + exactTitle;
};

/** Total score, or null when some query word matches nowhere (AND). */
const scoreEntry = (entry: IndexedArticle, queryWords: string[]): number | null => {
    let score = 0;
    for (const q of queryWords) {
        const s = wordScore(entry, q);
        if (s === 0) return null;
        score += s;
    }
    // An area article is the better landing page for a broad hit.
    return entry.article.type === 'bereich' ? score + 1 : score;
};

/** The heading matching the most query words, if any. */
const bestHeading = (entry: IndexedArticle, queryWords: string[]) => {
    let best: { h: IndexedArticle['headings'][number], hits: number } | undefined;
    entry.headings.forEach((h) => {
        const hits = queryWords.filter((q) => anyMatch(q, h.words)).length;
        if (hits > 0 && (!best || hits > best.hits)) best = { h, hits };
    });
    return best?.h;
};

export const searchHelp = (index: IndexedArticle[], query: string, limit = 20): SearchResult[] => {
    const queryWords = tokenize(query);
    if (!queryWords.length) return [];

    const results: SearchResult[] = [];
    for (const entry of index) {
        const score = scoreEntry(entry, queryWords);
        if (score === null) continue;
        // A title hit lands on the article itself; only otherwise jump to the
        // section that matched.
        const titleCoversQuery = queryWords.every((q) => anyMatch(q, entry.titleWords));
        const heading = titleCoversQuery ? undefined : bestHeading(entry, queryWords);
        results.push({
            article: entry.article,
            score,
            anchor: heading?.id,
            headingText: heading?.text,
            snippet: snippetAround(entry.bodyText, queryWords),
        });
    }
    results.sort((a, b) => b.score - a.score);
    return results.slice(0, limit);
};
